/**
 * Point the camera at a barcode, or type the number under it. Calls onCode once with a valid GTIN.
 *
 * A code is accepted only after the same number is read twice in a row (a single frame can
 * misread), the camera is asked for continuous focus, the torch can be switched on where the
 * camera has one, scanning pauses while the tab is hidden, and every start/stop is tracked so a
 * camera that finishes opening after the scanner closed is switched off straight away.
 */
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { parseGtin } from '@engine/barcode';
import { cameraErrorMessage, createDetector, type Detector } from '../lib/barcodeScanner';
import { log } from '../lib/logger';
import { Icon } from './Icon';
import { Spinner } from './ui';

const SCAN_EVERY_MS: number = 150;
/** The confirming read must come this soon after the first. */
const CONFIRM_WINDOW_MS: number = 1500;

type TrackCaps = MediaTrackCapabilities & { torch?: boolean; focusMode?: string[] };

export function BarcodeScanner({ onCode, autoStart = true }: { onCode: (code: string) => void; autoStart?: boolean }): ReactElement {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  /** Bumped by every start and stop; work from an older run gives up. */
  const runRef = useRef<number>(0);
  const [state, setState] = useState<'idle' | 'starting' | 'scanning' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [seen, setSeen] = useState<boolean>(false);
  const [torch, setTorch] = useState<{ supported: boolean; on: boolean }>({ supported: false, on: false });
  const [typed, setTyped] = useState<string>('');
  const [typedError, setTypedError] = useState<string | null>(null);
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;

  const stop = useCallback((): void => {
    runRef.current += 1;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const finish = useCallback((code: string): void => {
    stop();
    navigator.vibrate?.(60);
    onCodeRef.current(code);
  }, [stop]);

  const loop = useCallback(async (detector: Detector, video: HTMLVideoElement, run: number): Promise<void> => {
    let first: { code: string; at: number } | null = null;
    while (run === runRef.current) {
      if (document.visibilityState === 'visible' && video.readyState >= 2) {
        try {
          const found = await detector.detect(video);
          if (run !== runRef.current) return;
          for (const d of found) {
            const g = parseGtin(d.rawValue);
            if (!g.ok) continue;
            const now: number = performance.now();
            if (first && first.code === g.gtin && now - first.at <= CONFIRM_WINDOW_MS) { finish(g.gtin); return; }
            first = { code: g.gtin, at: now };
            setSeen(true);
            break;
          }
        } catch (err) {
          log.warn('barcode.detect_failed', { error: String(err) });
        }
      }
      await new Promise<void>((r) => setTimeout(r, SCAN_EVERY_MS));
    }
  }, [finish]);

  const start = useCallback(async (): Promise<void> => {
    stop();
    const run: number = runRef.current;
    setError(null);
    setSeen(false);
    setTorch({ supported: false, on: false });
    setState('starting');
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new DOMException('no mediaDevices', 'SecurityError');
      const [detector, stream] = await Promise.all([
        createDetector(),
        navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false }),
      ]);
      if (run !== runRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;
      const track: MediaStreamTrack | undefined = stream.getVideoTracks()[0];
      try {
        const caps: TrackCaps | undefined = track?.getCapabilities?.() as TrackCaps | undefined;
        if (caps?.focusMode?.includes('continuous')) await track!.applyConstraints({ advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet] });
        setTorch({ supported: caps?.torch === true, on: false });
      } catch (err) {
        log.info('barcode.camera_tuning_skipped', { error: String(err) });
      }
      const video: HTMLVideoElement | null = videoRef.current;
      if (!video) { stop(); return; }
      video.srcObject = stream;
      await video.play();
      if (run !== runRef.current) return;
      setState('scanning');
      void loop(detector, video, run);
    } catch (err) {
      if (run !== runRef.current) return;
      stop();
      log.warn('barcode.camera_failed', { error: String(err) });
      setError(cameraErrorMessage(err));
      setState('error');
    }
  }, [stop, loop]);

  const toggleTorch = async (): Promise<void> => {
    const track: MediaStreamTrack | undefined = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torch.on } as MediaTrackConstraintSet] });
      setTorch((t) => ({ ...t, on: !t.on }));
    } catch (err) {
      log.warn('barcode.torch_failed', { error: String(err) });
      setTorch({ supported: false, on: false });
    }
  };

  useEffect(() => {
    if (autoStart) void start();
    return () => stop();
  }, [autoStart, start, stop]);

  const submitTyped = (): void => {
    const g = parseGtin(typed);
    if (!g.ok) { setTypedError(g.reason); return; }
    setTypedError(null);
    finish(g.gtin);
  };

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className={`scan-box ${state}`}>
        <video ref={videoRef} playsInline muted aria-label="Camera preview" />
        {state === 'scanning' && <div className="scan-line" aria-hidden="true" />}
        {state === 'scanning' && torch.supported && (
          <button type="button" className={`scan-torch ${torch.on ? 'on' : ''}`} onClick={() => void toggleTorch()} aria-label={torch.on ? 'Turn the light off' : 'Turn the light on'}><Icon name="flash" size={18} /></button>
        )}
        {state === 'starting' && <div className="scan-msg"><Spinner /> Starting camera…</div>}
        {state === 'idle' && (
          <button type="button" className="btn btn-primary scan-msg" onClick={() => void start()}><Icon name="camera" size={18} /> Start camera</button>
        )}
        {state === 'error' && (
          <div className="scan-msg"><div>{error}</div><button type="button" className="btn btn-sm" onClick={() => void start()}>Try again</button></div>
        )}
      </div>
      {state === 'scanning' && (
        <p className="faint" style={{ margin: 0, fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
          {seen ? 'Got it, hold steady…' : 'Hold the barcode inside the frame, about a hand’s width away. It scans by itself.'}
        </p>
      )}
      <form className="input-row" onSubmit={(e) => { e.preventDefault(); submitTyped(); }}>
        <input className="input" inputMode="numeric" autoComplete="off" placeholder="Or type the number under the barcode" value={typed}
          onChange={(e) => setTyped(e.target.value.replace(/[^\d\s-]/g, ''))} aria-label="Barcode number" />
        <button type="submit" className="btn" disabled={typed.replace(/\D/g, '').length < 8}>Look up</button>
      </form>
      {typedError !== null && <div className="note warn">{typedError}</div>}
    </div>
  );
}
